import React, {
  useEffect,
  useState
} from "react";

import {
  Button,
  Checkbox,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  Paper,
  TextField,
  Typography
} from "@material-ui/core";

import {
  makeStyles
} from "@material-ui/core/styles";

import LocationOnOutlinedIcon
  from "@material-ui/icons/LocationOnOutlined";

import OpenInNewIcon
  from "@material-ui/icons/OpenInNew";

import BusinessIcon
  from "@material-ui/icons/Business";

import CheckCircleOutlineIcon
  from "@material-ui/icons/CheckCircleOutline";

import api from "../../services/api";

const DRAFT_KEY =
  "korax.localDiagnosticDraft";

const useStyles = makeStyles(theme => ({
  page: {
    minHeight: "calc(100vh - 80px)",
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(6)
  },

  card: {
    width: "100%",
    maxWidth: 720,
    margin: "0 auto",
    borderRadius: 20,
    padding: theme.spacing(4),
    boxShadow:
      "0 14px 42px rgba(15, 23, 42, 0.08)"
  },

  icon: {
    width: 64,
    height: 64,
    borderRadius: 18,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: theme.spacing(3),
    background: "#EEF5FF",
    color: "#1265D8"
  },

  title: {
    fontWeight: 800,
    marginBottom: theme.spacing(1)
  },

  subtitle: {
    color: "#64748B",
    lineHeight: 1.6
  },

  divider: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3)
  },

  sectionTitle: {
    fontWeight: 800,
    marginBottom: theme.spacing(0.5)
  },

  sectionText: {
    color: "#64748B",
    lineHeight: 1.5,
    marginBottom: theme.spacing(2)
  },

  mainButton: {
    minHeight: 56,
    borderRadius: 12,
    fontWeight: 800
  },

  secondaryButton: {
    minHeight: 52,
    borderRadius: 12,
    fontWeight: 800,
    marginTop: theme.spacing(1.5)
  },

  successBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: 12,
    background: "#F0FDF4",
    color: "#166534"
  },

  errorBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: 12,
    background: "#FEF2F2",
    color: "#991B1B"
  },

  form: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2)
  },

  mapsBox: {
    padding: theme.spacing(2.5),
    borderRadius: 16,
    border: "1px solid #DBEAFE",
    background: "#F8FBFF"
  },

  selectedBox: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2.5),
    borderRadius: 16,
    border: "1px solid #BBF7D0",
    background: "#F0FDF4"
  },

  selectedHeader: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    color: "#166534",
    marginBottom: theme.spacing(1)
  },

  selectedName: {
    fontWeight: 800
  },

  checklistBox: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2.5),
    borderRadius: 16,
    border: "1px solid #E2E8F0",
    background: "#FFFFFF"
  },

  checklistItem: {
    width: "100%",
    marginLeft: 0,
    marginRight: 0,
    marginBottom: theme.spacing(0.5),
    padding: theme.spacing(0.5, 0),
    borderBottom: "1px solid #F1F5F9"
  },

  numericFields: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),

    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr"
    }
  },

  reportBox: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(3),
    borderRadius: 18,
    border: "1px solid #BFDBFE",
    background:
      "linear-gradient(180deg, #F8FBFF 0%, #FFFFFF 100%)"
  },

  scoreCircle: {
    width: 126,
    height: 126,
    borderRadius: "50%",
    margin: "20px auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    background: "#0F2B68",
    color: "#FFFFFF",
    boxShadow:
      "0 12px 30px rgba(15, 43, 104, 0.20)"
  },

  scoreNumber: {
    fontSize: 42,
    fontWeight: 900,
    lineHeight: 1
  },

  scoreLabel: {
    fontSize: 13,
    opacity: 0.8,
    marginTop: 5
  },

  diagnosticStatus: {
    textAlign: "center",
    fontWeight: 800,
    marginBottom: theme.spacing(2)
  },

  opportunity: {
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(1),
    borderRadius: 10,
    background: "#F8FAFC",
    border: "1px solid #E2E8F0"
  },

  loading: {
    minHeight: 300,
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },

  blocked: {
    color: "#64748B"
  }
}));

const getLocationError = error => {
  if (error?.code === 1) {
    return (
      "A localização está bloqueada no navegador. " +
      "Autorize a localização e tente novamente."
    );
  }

  if (error?.code === 2) {
    return (
      "Não foi possível determinar sua localização."
    );
  }

  if (error?.code === 3) {
    return (
      "A localização demorou para responder. Tente novamente."
    );
  }

  return (
    "Não foi possível obter sua localização."
  );
};

const LocalDiagnostic = () => {
  const classes = useStyles();

  const [
    loadingAccess,
    setLoadingAccess
  ] = useState(true);

  const [
    enabled,
    setEnabled
  ] = useState(false);

  const [
    accessError,
    setAccessError
  ] = useState(false);

  const [
    locating,
    setLocating
  ] = useState(false);

  const [
    currentLocation,
    setCurrentLocation
  ] = useState(null);

  const [
    locationError,
    setLocationError
  ] = useState("");

  const [
    businessName,
    setBusinessName
  ] = useState("");

  const [
    businessAddress,
    setBusinessAddress
  ] = useState("");

  const [
    businessCategory,
    setBusinessCategory
  ] = useState("");

  const [
    formError,
    setFormError
  ] = useState("");

  const [
    selectedBusiness,
    setSelectedBusiness
  ] = useState(null);

  const [
    diagnostic,
    setDiagnostic
  ] = useState({
    profileFound: true,
    addressComplete: false,
    phoneVisible: false,
    hoursVisible: false,
    digitalChannel: false,
    recentPhotos: false,
    rating: "",
    reviewCount: ""
  });

  const [
    generatedReport,
    setGeneratedReport
  ] = useState(null);

  const [
    savingDiagnostic,
    setSavingDiagnostic
  ] = useState(false);

  const [
    diagnosticError,
    setDiagnosticError
  ] = useState("");

  useEffect(() => {
    let mounted = true;

    const loadStatus = async () => {
      try {
        const { data } =
          await api.get(
            "/local-diagnostic/status"
          );

        if (!mounted) return;

        setEnabled(
          Boolean(data?.enabled)
        );
      } catch (error) {
        if (!mounted) return;

        console.error(
          "[LocalDiagnostic] status:",
          error
        );

        setAccessError(true);
      } finally {
        if (mounted) {
          setLoadingAccess(false);
        }
      }
    };

    loadStatus();

    try {
      const saved =
        sessionStorage.getItem(
          DRAFT_KEY
        );

      if (saved) {
        const draft =
          JSON.parse(saved);

        setBusinessName(
          draft.businessName || ""
        );

        setBusinessAddress(
          draft.businessAddress || ""
        );

        setBusinessCategory(
          draft.businessCategory || ""
        );

        if (
          Number.isFinite(
            draft.latitude
          ) &&
          Number.isFinite(
            draft.longitude
          )
        ) {
          setCurrentLocation({
            latitude:
              draft.latitude,
            longitude:
              draft.longitude
          });
        }
      }
    } catch (error) {
      console.warn(
        "[LocalDiagnostic] draft:",
        error
      );
    }

    return () => {
      mounted = false;
    };
  }, []);

  const persistDraft = values => {
    try {
      sessionStorage.setItem(
        DRAFT_KEY,
        JSON.stringify(values)
      );
    } catch (error) {
      console.warn(
        "[LocalDiagnostic] persist draft:",
        error
      );
    }
  };

  const handleUseLocation = () => {
    setLocationError("");
    setLocating(true);

    if (!navigator.geolocation) {
      setLocating(false);

      setLocationError(
        "Este navegador não oferece suporte à localização."
      );

      return;
    }

    navigator.geolocation.getCurrentPosition(
      position => {
        const latitude =
          position.coords.latitude;

        const longitude =
          position.coords.longitude;

        const location = {
          latitude,
          longitude
        };

        setCurrentLocation(
          location
        );

        persistDraft({
          businessName,
          businessAddress,
          businessCategory,
          latitude,
          longitude
        });

        setLocating(false);
      },

      error => {
        setLocating(false);

        setLocationError(
          getLocationError(
            error
          )
        );
      },

      {
        enableHighAccuracy: true,
        timeout: 12000,
        maximumAge: 30000
      }
    );
  };

  const handleOpenNearbyMaps = () => {
    let url =
      "https://www.google.com/maps/@?api=1&map_action=map";

    if (currentLocation) {
      const center =
        `${currentLocation.latitude},${currentLocation.longitude}`;

      url +=
        `&center=${encodeURIComponent(center)}` +
        "&zoom=18";
    }

    url +=
      "&utm_source=korax" +
      "&utm_campaign=local_diagnostic";

    persistDraft({
      businessName,
      businessAddress,
      businessCategory,
      latitude:
        currentLocation?.latitude,
      longitude:
        currentLocation?.longitude
    });

    window.open(
      url,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const handleSearchNameOnMaps = () => {
    const query =
      businessName.trim();

    if (!query) {
      setFormError(
        "Digite o nome da empresa primeiro."
      );

      return;
    }

    setFormError("");

    const url =
      "https://www.google.com/maps/search/" +
      `?api=1&query=${encodeURIComponent(query)}` +
      "&utm_source=korax" +
      "&utm_campaign=local_diagnostic";

    persistDraft({
      businessName,
      businessAddress,
      businessCategory,
      latitude:
        currentLocation?.latitude,
      longitude:
        currentLocation?.longitude
    });

    window.open(
      url,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const calculateDiagnosticScore =
    data => {
      let score = 0;

      if (data.profileFound) {
        score += 15;
      }

      if (data.addressComplete) {
        score += 10;
      }

      if (data.phoneVisible) {
        score += 10;
      }

      if (data.hoursVisible) {
        score += 10;
      }

      if (data.digitalChannel) {
        score += 15;
      }

      const rating =
        Number(data.rating);

      if (
        Number.isFinite(rating) &&
        rating > 0
      ) {
        if (rating >= 4.5) {
          score += 15;
        } else if (rating >= 4) {
          score += 10;
        } else {
          score += 5;
        }
      }

      const reviews =
        Number(data.reviewCount);

      if (
        Number.isFinite(reviews) &&
        reviews > 0
      ) {
        if (reviews >= 100) {
          score += 15;
        } else if (reviews >= 30) {
          score += 10;
        } else if (reviews >= 10) {
          score += 6;
        } else {
          score += 3;
        }
      }

      if (data.recentPhotos) {
        score += 10;
      }

      return Math.min(
        100,
        score
      );
    };

  const getDiagnosticStatus =
    score => {
      if (score >= 80) {
        return "Presença digital forte";
      }

      if (score >= 60) {
        return "Presença digital intermediária";
      }

      if (score >= 40) {
        return "Presença digital em atenção";
      }

      return "Presença digital fraca";
    };

  const buildOpportunities =
    data => {
      const opportunities = [];

      if (!data.profileFound) {
        opportunities.push(
          "Criar ou estruturar corretamente o Perfil da Empresa no Google."
        );
      }

      if (!data.addressComplete) {
        opportunities.push(
          "Completar e revisar as informações de localização da empresa."
        );
      }

      if (!data.phoneVisible) {
        opportunities.push(
          "Facilitar o contato exibindo telefone ou WhatsApp de forma clara."
        );
      }

      if (!data.hoursVisible) {
        opportunities.push(
          "Manter horários de funcionamento completos e atualizados."
        );
      }

      if (!data.digitalChannel) {
        opportunities.push(
          "Criar um caminho digital claro para transformar buscas em contatos."
        );
      }

      const rating =
        Number(data.rating);

      if (
        !Number.isFinite(rating) ||
        rating <= 0 ||
        rating < 4.5
      ) {
        opportunities.push(
          "Fortalecer a reputação digital com uma estratégia recorrente de avaliações."
        );
      }

      const reviews =
        Number(data.reviewCount);

      if (
        !Number.isFinite(reviews) ||
        reviews < 30
      ) {
        opportunities.push(
          "Aumentar o volume de avaliações para gerar mais prova social."
        );
      }

      if (!data.recentPhotos) {
        opportunities.push(
          "Adicionar fotos recentes para transmitir atividade e confiança."
        );
      }

      if (
        opportunities.length === 0
      ) {
        opportunities.push(
          "Criar uma estratégia para captar novas avaliações de forma recorrente."
        );

        opportunities.push(
          "Usar QR Code e NFC para encurtar o caminho entre o cliente físico e os canais digitais."
        );

        opportunities.push(
          "Centralizar os principais canais digitais em uma experiência simples para o cliente."
        );
      }

      return opportunities.slice(
        0,
        3
      );
    };

  const handleGenerateDiagnostic =
    async () => {
      if (
        !selectedBusiness ||
        savingDiagnostic
      ) {
        return;
      }

      setSavingDiagnostic(true);
      setDiagnosticError("");

      try {
        const { data } =
          await api.post(
            "/local-diagnostic/diagnostics",
            {
              businessName:
                selectedBusiness.businessName,

              businessAddress:
                selectedBusiness.formattedAddress,

              businessCategory:
                selectedBusiness.primaryType,

              latitude:
                selectedBusiness.latitude,

              longitude:
                selectedBusiness.longitude,

              profileFound:
                diagnostic.profileFound,

              addressComplete:
                diagnostic.addressComplete,

              phoneVisible:
                diagnostic.phoneVisible,

              hoursVisible:
                diagnostic.hoursVisible,

              digitalChannel:
                diagnostic.digitalChannel,

              recentPhotos:
                diagnostic.recentPhotos,

              rating:
                diagnostic.rating === ""
                  ? null
                  : Number(
                      diagnostic.rating
                    ),

              reviewCount:
                diagnostic.reviewCount === ""
                  ? null
                  : Number(
                      diagnostic.reviewCount
                    )
            }
          );

        const saved =
          data?.diagnostic;

        if (!saved?.code) {
          throw new Error(
            "INVALID_DIAGNOSTIC_RESPONSE"
          );
        }

        setGeneratedReport({
          ...saved,

          status:
            saved.classification ||
            "Diagnóstico concluído",

          opportunities:
            Array.isArray(
              saved.opportunities
            )
              ? saved.opportunities
              : []
        });
      } catch (error) {
        console.error(
          "[LocalDiagnostic] save diagnostic:",
          error
        );

        setDiagnosticError(
          error?.response?.data?.message ||
            "Não foi possível salvar o Raio-X. Tente novamente."
        );
      } finally {
        setSavingDiagnostic(false);
      }
    };

  const handleSelectBusiness = () => {
    const name =
      businessName.trim();

    if (name.length < 2) {
      setFormError(
        "Informe o nome da empresa."
      );

      return;
    }

    const business = {
      businessName: name,

      formattedAddress:
        businessAddress.trim() ||
        null,

      primaryType:
        businessCategory.trim() ||
        null,

      latitude:
        currentLocation?.latitude ||
        null,

      longitude:
        currentLocation?.longitude ||
        null,

      source:
        "GOOGLE_MAPS_MANUAL"
    };

    setSelectedBusiness(
      business
    );

    setGeneratedReport(null);
    setDiagnosticError("");

    setDiagnostic({
      profileFound: true,
      addressComplete:
        Boolean(
          businessAddress.trim()
        ),
      phoneVisible: false,
      hoursVisible: false,
      digitalChannel: false,
      recentPhotos: false,
      rating: "",
      reviewCount: ""
    });

    setFormError("");

    persistDraft({
      businessName: name,
      businessAddress,
      businessCategory,

      latitude:
        currentLocation?.latitude,

      longitude:
        currentLocation?.longitude
    });
  };

  if (loadingAccess) {
    return (
      <Container
        className={classes.page}
      >
        <Paper
          className={classes.card}
        >
          <div
            className={
              classes.loading
            }
          >
            <CircularProgress />
          </div>
        </Paper>
      </Container>
    );
  }

  if (
    accessError ||
    !enabled
  ) {
    return (
      <Container
        className={classes.page}
      >
        <Paper
          className={classes.card}
        >
          <Typography
            variant="h5"
            className={classes.title}
          >
            Raio-X Digital
          </Typography>

          <Typography
            className={
              classes.blocked
            }
          >
            {accessError
              ? "Não foi possível validar o acesso ao módulo."
              : "Este módulo ainda não está habilitado para esta operação."}
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container
      className={classes.page}
    >
      <Paper
        className={classes.card}
      >
        <div
          className={classes.icon}
        >
          <LocationOnOutlinedIcon
            fontSize="large"
          />
        </div>

        <Typography
          variant="h4"
          className={classes.title}
        >
          Raio-X Digital
        </Typography>

        <Typography
          className={
            classes.subtitle
          }
        >
          Localize o negócio,
          confirme os dados e gere
          um diagnóstico comercial
          de presença digital.
        </Typography>

        <Divider
          className={
            classes.divider
          }
        />

        <Typography
          variant="h6"
          className={
            classes.sectionTitle
          }
        >
          1. Localize o negócio
        </Typography>

        <Typography
          className={
            classes.sectionText
          }
        >
          Use sua localização e
          abra o Google Maps para
          encontrar o estabelecimento.
        </Typography>

        <div
          className={
            classes.mapsBox
          }
        >
          <Button
            fullWidth
            variant="contained"
            color="primary"
            disabled={locating}
            className={
              classes.mainButton
            }
            startIcon={
              locating ? (
                <CircularProgress
                  size={20}
                  color="inherit"
                />
              ) : (
                <LocationOnOutlinedIcon />
              )
            }
            onClick={
              handleUseLocation
            }
          >
            {locating
              ? "OBTENDO LOCALIZAÇÃO..."
              : currentLocation
              ? "LOCALIZAÇÃO CONFIRMADA"
              : "USAR MINHA LOCALIZAÇÃO"}
          </Button>

          {currentLocation && (
            <div
              className={
                classes.successBox
              }
            >
              Localização pronta.
              Agora abra o Google
              Maps e encontre a
              empresa.
            </div>
          )}

          {locationError && (
            <div
              className={
                classes.errorBox
              }
            >
              {locationError}
            </div>
          )}

          <Button
            fullWidth
            variant="outlined"
            color="primary"
            className={
              classes.secondaryButton
            }
            startIcon={
              <OpenInNewIcon />
            }
            onClick={
              handleOpenNearbyMaps
            }
          >
            ABRIR GOOGLE MAPS
          </Button>
        </div>

        <Divider
          className={
            classes.divider
          }
        />

        <Typography
          variant="h6"
          className={
            classes.sectionTitle
          }
        >
          2. Informe a empresa
        </Typography>

        <Typography
          className={
            classes.sectionText
          }
        >
          Depois de localizar o
          estabelecimento no Maps,
          volte para a Korax e
          confirme os dados abaixo.
        </Typography>

        <div
          className={classes.form}
        >
          <TextField
            fullWidth
            required
            variant="outlined"
            label="Nome da empresa"
            placeholder="Ex: Barbearia João"
            value={businessName}
            onChange={event =>
              setBusinessName(
                event.target.value
              )
            }
          />

          <Button
            fullWidth
            variant="outlined"
            color="primary"
            startIcon={
              <OpenInNewIcon />
            }
            onClick={
              handleSearchNameOnMaps
            }
          >
            BUSCAR ESTE NOME NO GOOGLE MAPS
          </Button>

          <TextField
            fullWidth
            variant="outlined"
            label="Endereço"
            placeholder="Opcional"
            value={businessAddress}
            onChange={event =>
              setBusinessAddress(
                event.target.value
              )
            }
          />

          <TextField
            fullWidth
            variant="outlined"
            label="Categoria"
            placeholder="Ex: Barbearia, clínica, restaurante"
            value={businessCategory}
            onChange={event =>
              setBusinessCategory(
                event.target.value
              )
            }
          />

          <Button
            fullWidth
            variant="contained"
            color="primary"
            className={
              classes.mainButton
            }
            startIcon={
              <BusinessIcon />
            }
            onClick={
              handleSelectBusiness
            }
          >
            SELECIONAR EMPRESA
          </Button>
        </div>

        {formError && (
          <div
            className={
              classes.errorBox
            }
          >
            {formError}
          </div>
        )}

        {selectedBusiness && (
          <div
            className={
              classes.selectedBox
            }
          >
            <div
              className={
                classes.selectedHeader
              }
            >
              <CheckCircleOutlineIcon />

              <strong>
                Empresa selecionada
              </strong>
            </div>

            <Typography
              className={
                classes.selectedName
              }
            >
              {
                selectedBusiness.businessName
              }
            </Typography>

            {selectedBusiness.formattedAddress && (
              <Typography>
                {
                  selectedBusiness.formattedAddress
                }
              </Typography>
            )}

            {selectedBusiness.primaryType && (
              <Typography>
                {
                  selectedBusiness.primaryType
                }
              </Typography>
            )}

            <Typography
              variant="body2"
              style={{
                marginTop: 12,
                color: "#166534"
              }}
            >
              Pronto para iniciar
              o checklist do
              Raio-X Digital.
            </Typography>
          </div>
        )}

        {selectedBusiness && (
          <>
            <Divider
              className={
                classes.divider
              }
            />

            <Typography
              variant="h6"
              className={
                classes.sectionTitle
              }
            >
              3. Checklist do Raio-X
            </Typography>

            <Typography
              className={
                classes.sectionText
              }
            >
              Confira no Google Maps
              os sinais abaixo. O score
              será calculado somente com
              informações observadas.
            </Typography>

            <div
              className={
                classes.checklistBox
              }
            >
              <FormControlLabel
                className={
                  classes.checklistItem
                }
                control={
                  <Checkbox
                    color="primary"
                    checked={
                      diagnostic.profileFound
                    }
                    onChange={event =>
                      setDiagnostic(
                        previous => ({
                          ...previous,
                          profileFound:
                            event.target.checked
                        })
                      )
                    }
                  />
                }
                label="Perfil da empresa encontrado no Google"
              />

              <FormControlLabel
                className={
                  classes.checklistItem
                }
                control={
                  <Checkbox
                    color="primary"
                    checked={
                      diagnostic.addressComplete
                    }
                    onChange={event =>
                      setDiagnostic(
                        previous => ({
                          ...previous,
                          addressComplete:
                            event.target.checked
                        })
                      )
                    }
                  />
                }
                label="Endereço completo e aparentemente correto"
              />

              <FormControlLabel
                className={
                  classes.checklistItem
                }
                control={
                  <Checkbox
                    color="primary"
                    checked={
                      diagnostic.phoneVisible
                    }
                    onChange={event =>
                      setDiagnostic(
                        previous => ({
                          ...previous,
                          phoneVisible:
                            event.target.checked
                        })
                      )
                    }
                  />
                }
                label="Telefone ou WhatsApp visível"
              />

              <FormControlLabel
                className={
                  classes.checklistItem
                }
                control={
                  <Checkbox
                    color="primary"
                    checked={
                      diagnostic.hoursVisible
                    }
                    onChange={event =>
                      setDiagnostic(
                        previous => ({
                          ...previous,
                          hoursVisible:
                            event.target.checked
                        })
                      )
                    }
                  />
                }
                label="Horários de funcionamento preenchidos"
              />

              <FormControlLabel
                className={
                  classes.checklistItem
                }
                control={
                  <Checkbox
                    color="primary"
                    checked={
                      diagnostic.digitalChannel
                    }
                    onChange={event =>
                      setDiagnostic(
                        previous => ({
                          ...previous,
                          digitalChannel:
                            event.target.checked
                        })
                      )
                    }
                  />
                }
                label="Possui site, rede social ou outro canal digital acessível"
              />

              <FormControlLabel
                className={
                  classes.checklistItem
                }
                control={
                  <Checkbox
                    color="primary"
                    checked={
                      diagnostic.recentPhotos
                    }
                    onChange={event =>
                      setDiagnostic(
                        previous => ({
                          ...previous,
                          recentPhotos:
                            event.target.checked
                        })
                      )
                    }
                  />
                }
                label="Possui fotos recentes no perfil"
              />

              <div
                className={
                  classes.numericFields
                }
              >
                <TextField
                  fullWidth
                  variant="outlined"
                  type="number"
                  label="Nota no Google"
                  placeholder="Ex: 4.7"
                  inputProps={{
                    min: 0,
                    max: 5,
                    step: 0.1
                  }}
                  value={
                    diagnostic.rating
                  }
                  onChange={event =>
                    setDiagnostic(
                      previous => ({
                        ...previous,
                        rating:
                          event.target.value
                      })
                    )
                  }
                />

                <TextField
                  fullWidth
                  variant="outlined"
                  type="number"
                  label="Quantidade de avaliações"
                  placeholder="Ex: 47"
                  inputProps={{
                    min: 0,
                    step: 1
                  }}
                  value={
                    diagnostic.reviewCount
                  }
                  onChange={event =>
                    setDiagnostic(
                      previous => ({
                        ...previous,
                        reviewCount:
                          event.target.value
                      })
                    )
                  }
                />
              </div>

              <Button
                fullWidth
                variant="contained"
                color="primary"
                className={
                  classes.mainButton
                }
                style={{
                  marginTop: 24
                }}
                disabled={
                  savingDiagnostic
                }
                onClick={
                  handleGenerateDiagnostic
                }
              >
                {savingDiagnostic
                  ? "SALVANDO RAIO-X..."
                  : "GERAR RAIO-X DIGITAL"}
              </Button>
            </div>

            {diagnosticError && (
              <div
                className={
                  classes.errorBox
                }
              >
                {diagnosticError}
              </div>
            )}

            {generatedReport && (
              <div
                className={
                  classes.reportBox
                }
              >
                <Typography
                  variant="h5"
                  align="center"
                  className={
                    classes.title
                  }
                >
                  Raio-X Digital
                </Typography>

                <Typography
                  align="center"
                  color="textSecondary"
                >
                  {
                    selectedBusiness.businessName
                  }
                </Typography>

                {generatedReport.code && (
                  <Typography
                    align="center"
                    style={{
                      marginTop: 12,
                      fontWeight: 700,
                      color: "#0F2B68"
                    }}
                  >
                    Código do diagnóstico:{" "}
                    <strong>
                      {generatedReport.code}
                    </strong>
                  </Typography>
                )}

                <div
                  className={
                    classes.scoreCircle
                  }
                >
                  <div
                    className={
                      classes.scoreNumber
                    }
                  >
                    {
                      generatedReport.score
                    }
                  </div>

                  <div
                    className={
                      classes.scoreLabel
                    }
                  >
                    de 100
                  </div>
                </div>

                <Typography
                  variant="h6"
                  className={
                    classes.diagnosticStatus
                  }
                >
                  {
                    generatedReport.status
                  }
                </Typography>

                <Typography
                  variant="subtitle1"
                  style={{
                    fontWeight: 800,
                    marginTop: 20
                  }}
                >
                  Principais oportunidades
                </Typography>

                {
                  generatedReport.opportunities.map(
                    (
                      opportunity,
                      index
                    ) => (
                      <div
                        key={
                          `${index}-${opportunity}`
                        }
                        className={
                          classes.opportunity
                        }
                      >
                        <strong>
                          {index + 1}.
                        </strong>{" "}
                        {opportunity}
                      </div>
                    )
                  )
                }

                <Typography
                  variant="body2"
                  color="textSecondary"
                  style={{
                    marginTop: 20,
                    lineHeight: 1.6
                  }}
                >
                  Este score representa
                  sinais observados no
                  perfil digital da empresa
                  no momento da avaliação.
                </Typography>
              </div>
            )}
          </>
        )}

      </Paper>
    </Container>
  );
};

export default LocalDiagnostic;
